import Badge from '@codegouvfr/react-dsfr/Badge';
import Button from '@codegouvfr/react-dsfr/Button';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import clsx from 'clsx';
import { sumBy } from 'lodash-es';
import { getCompletionRate } from 'maestro-shared/schema/LocalPrescription/LocalPrescription';
import {
  ContextLabels,
  type ProgrammingPlanContext
} from 'maestro-shared/schema/ProgrammingPlan/Context';
import type { ProgrammingPlanChecked } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlans';
import { isPPVSubPlan } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingSubPlan';
import { useContext } from 'react';
import { pluralize } from 'src/utils/stringUtils';
import ProgrammingPlanMap from 'src/views/DashboardView/ProgrammingPlanMap';
import { AuthenticatedAppRoutes } from '../../AppRoutes';
import { CircleProgress } from '../../components/CircleProgress/CircleProgress';
import { ApiClientContext } from '../../services/apiClient';

interface ProgrammingPlanCardProps {
  programmingPlan: ProgrammingPlanChecked;
  context: ProgrammingPlanContext;
}

const ProgrammingPlanCard = ({
  programmingPlan,
  context
}: ProgrammingPlanCardProps) => {
  const apiClient = useContext(ApiClientContext);

  const isPPV = programmingPlan.subPlans.some((sp) => isPPVSubPlan(sp));

  const { data: regionalPrescriptions } =
    apiClient.useFindLocalPrescriptionsQuery({
      programmingPlanIds: [programmingPlan.id],
      contexts: [context],
      includes: ['sampleCounts']
    });

  const { data: complianceStats } = apiClient.useGetComplianceStatsQuery({
    programmingPlanId: programmingPlan.id,
    context,
    byDepartment: !isPPV
  });

  const compliantSampleCount = sumBy(complianceStats, 'compliantCount');
  const nonCompliantSampleCount = sumBy(complianceStats, 'nonCompliantCount');

  return (
    <>
      <div
        className={clsx(
          'white-container',
          cx('fr-px-4w', 'fr-pt-3w', 'fr-pb-2w')
        )}
        style={{
          borderBottom: 'none'
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div>
            <h4>{ContextLabels[context]}</h4>
            <h3>
              {sumBy(regionalPrescriptions, 'sampleCount')}{' '}
              {pluralize(sumBy(regionalPrescriptions, 'sampleCount'))(
                'prélèvement'
              )}
              <br />
              {pluralize(sumBy(regionalPrescriptions, 'sampleCount'))(
                'programmé'
              )}
            </h3>
          </div>
          <CircleProgress
            progress={getCompletionRate(regionalPrescriptions ?? [])}
            sizePx={110}
            type={'percentage'}
          />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span>
            {sumBy(regionalPrescriptions, 'realizedSampleCount')}{' '}
            {pluralize(sumBy(regionalPrescriptions, 'realizedSampleCount'))(
              'prélèvement réalisé'
            )}
          </span>
          <span style={{ width: 110, textAlign: 'center' }}>de l'objectif</span>
        </div>
        {compliantSampleCount > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Badge severity="success" className={'fr-mt-2w'}>
              {pluralize(compliantSampleCount, {
                preserveCount: true
              })('prélèvement conforme')}
            </Badge>
          </div>
        )}
        {nonCompliantSampleCount > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Badge severity="error" className={'fr-mt-2w'}>
              {pluralize(nonCompliantSampleCount, {
                preserveCount: true,
                ignores: ['non']
              })('prélèvement non conforme')}
            </Badge>
          </div>
        )}
        <hr className={cx('fr-mt-4w', 'fr-mb-2w')} />
        <div className={clsx('d-flex-justify-center', cx('fr-col-12'))}>
          <Button
            priority="tertiary no outline"
            iconId="fr-icon-bar-chart-box-line"
            linkProps={{
              to: AuthenticatedAppRoutes.ProgrammingRoute.link({
                year: programmingPlan.year,
                contexts: [context]
              })
            }}
          >
            {[ContextLabels[context], programmingPlan.year].join(' ')}
          </Button>
        </div>
      </div>

      {!isPPV && (
        <div className={clsx('border', cx('fr-p-2w'))}>
          <ProgrammingPlanMap
            programmingPlan={programmingPlan}
            context={context}
            localPrescriptions={regionalPrescriptions ?? []}
          />
        </div>
      )}
    </>
  );
};

export default ProgrammingPlanCard;
